import PdModalConfirm from '@/js/App/PdModal/PdModalConfirm'
import { BeforeEvent, Extension, InteractionEvent, Naja } from 'naja'

declare module 'naja' {
	interface Options {
		pdModalConfirm?: boolean
		pdModalConfirmed?: boolean
	}
}

export class PdModalConfirmExtension implements Extension {
	private naja: Naja | undefined

	public initialize(naja: Naja): void {
		this.naja = naja

		naja.uiHandler.addEventListener('interaction', this.checkExtensionEnabled.bind(this), false)
		naja.addEventListener('before', this.hijackSpinner.bind(this))
	}

	private async checkExtensionEnabled(event: InteractionEvent): Promise<void> {
		const { element, originalEvent, options } = event.detail

		const shouldConfirm = element.getAttribute('data-confirm-content')

		// If this extension is disabled for the request, or if the request has already been confirmed, just continue.
		if (!shouldConfirm || options.pdModalConfirmed) {
			options.pdModalConfirm = shouldConfirm !== null
			return
		}

		// As the confirmation is asynchronous, we must always stop the naja from executing the request.
		event.preventDefault()

		// The cancelled submit event would otherwise bubble to the `document` listener of `SingleSubmitExtension`,
		// which handles it as a non-ajax submission and disables the form buttons for its whole timeout.
		if (originalEvent instanceof SubmitEvent) {
			originalEvent.stopPropagation()
		}

		// Then we wait for the user interaction with our custom confirm dialog.
		const confirmed = await PdModalConfirm.confirm(element as HTMLElement)

		if (!confirmed || !this.naja) {
			return
		}

		// And if it is confirmed, we trigger the same interaction on the original item, only this time with a custom
		// flag. Naja handles only native events, so a re-dispatched synthetic event wouldn't send any request.
		const confirmedOptions = this.naja.prepareOptions({ pdModalConfirmed: true })
		const request =
			element instanceof HTMLAnchorElement
				? this.naja.uiHandler.clickElement(element, confirmedOptions)
				: this.naja.uiHandler.submitForm(element as HTMLElement, confirmedOptions)

		// Errors are handled by naja `error` event listeners, same as for the requests started by naja itself.
		request.catch(() => {})
	}

	private hijackSpinner(event: BeforeEvent): void {
		const { options } = event.detail

		if (!options.pdModalConfirm) {
			return
		}

		options.spinnerInitiator = undefined
		options.btnSpinnerInitiator = PdModalConfirm.acceptButton
	}
}
